import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowRight, ArrowLeft, Loader2, Check, Building2, UserRound, Layers } from "lucide-react";

const INDUSTRIES = ["Recruitment", "Logistics", "Real Estate", "IT Services", "Digital Agencies", "Education", "Consulting", "Manufacturing", "Travel", "Retail", "Professional Services"];
const EMPLOYEES = ["1-10", "11-50", "51-200", "201-500", "500+"];
const COUNTRIES = ["India", "United States", "United Kingdom", "UAE", "Singapore", "Australia"];

const STEPS = [
  { n: 1, label: "Company", icon: Building2 },
  { n: 2, label: "Admin", icon: UserRound },
  { n: 3, label: "Template", icon: Layers },
];

export default function Signup() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    company_name: "", industry: "IT Services", employees: "11-50", country: "India", gstin: "",
    name: "", email: "", password: "", template: "IT Services",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const next = () => {
    setError("");
    if (step === 1 && !form.company_name.trim()) return setError("Please enter your company name.");
    if (step === 2) {
      if (!form.name.trim() || !form.email.trim()) return setError("Please enter your name and email.");
      if (form.password.length < 6) return setError("Password must be at least 6 characters.");
    }
    setStep((s) => Math.min(3, s + 1));
  };

  const submit = async () => {
    setError("");
    setLoading(true);
    const res = await register(form);
    setLoading(false);
    if (res.ok) {
      toast.success("Your Blueconnect workspace is ready! 🎉");
      navigate("/app");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="absolute inset-0 bc-grid-bg opacity-30" />
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="relative w-full max-w-lg">
        <div className="mb-6 flex justify-center"><Link to="/"><Logo /></Link></div>
        <div className="rounded-2xl border border-border bg-card p-6 shadow-xl sm:p-8">
          {/* Stepper */}
          <div className="mb-8 flex items-center justify-between">
            {STEPS.map((s, i) => (
              <React.Fragment key={s.n}>
                <div className="flex flex-col items-center gap-1.5">
                  <div className={`flex h-9 w-9 items-center justify-center rounded-full border-2 text-sm font-semibold transition-colors ${
                    step > s.n ? "border-success bg-success text-white" : step === s.n ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground"}`}>
                    {step > s.n ? <Check className="h-4 w-4" /> : <s.icon className="h-4 w-4" />}
                  </div>
                  <span className={`text-[11px] font-medium ${step >= s.n ? "text-foreground" : "text-muted-foreground"}`}>{s.label}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`mx-2 h-0.5 flex-1 rounded ${step > s.n ? "bg-success" : "bg-border"}`} />}
              </React.Fragment>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }} className="space-y-4">
              {step === 1 && (
                <>
                  <h1 className="font-heading text-xl font-bold">Tell us about your company</h1>
                  <div className="space-y-1.5">
                    <Label>Company name</Label>
                    <Input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} placeholder="Blueorbit Corporation" data-testid="signup-company-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Industry</Label>
                      <Select value={form.industry} onValueChange={(v) => { set("industry", v); set("template", v); }}>
                        <SelectTrigger data-testid="signup-industry-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Employees</Label>
                      <Select value={form.employees} onValueChange={(v) => set("employees", v)}>
                        <SelectTrigger data-testid="signup-employees-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{EMPLOYEES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Country</Label>
                      <Select value={form.country} onValueChange={(v) => set("country", v)}>
                        <SelectTrigger data-testid="signup-country-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{COUNTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>GSTIN <span className="text-muted-foreground">(optional)</span></Label>
                      <Input value={form.gstin} onChange={(e) => set("gstin", e.target.value)} placeholder="29ABCDE1234F1Z5" data-testid="signup-gstin-input" />
                    </div>
                  </div>
                </>
              )}
              {step === 2 && (
                <>
                  <h1 className="font-heading text-xl font-bold">Create your admin account</h1>
                  <div className="space-y-1.5">
                    <Label>Full name</Label>
                    <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Aarav Mehta" data-testid="signup-name-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Work email</Label>
                    <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@company.com" data-testid="signup-email-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Password</Label>
                    <Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="At least 6 characters" data-testid="signup-password-input" />
                  </div>
                </>
              )}
              {step === 3 && (
                <>
                  <h1 className="font-heading text-xl font-bold">Choose your starting template</h1>
                  <p className="text-sm text-muted-foreground">We'll pre-configure pipelines and workflows for your industry.</p>
                  <div className="grid max-h-72 grid-cols-2 gap-2 overflow-y-auto pr-1">
                    {INDUSTRIES.map((i) => (
                      <button key={i} type="button" onClick={() => set("template", i)} data-testid={`signup-template-${i.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`rounded-xl border p-3 text-left text-sm font-medium transition-colors ${form.template === i ? "border-primary bg-primary/5 text-primary" : "border-border hover:border-primary/40"}`}>
                        {i}
                      </button>
                    ))}
                  </div>
                </>
              )}
              {error && <p className="rounded-lg bg-critical/10 px-3 py-2 text-sm text-critical" data-testid="signup-error">{error}</p>}
            </motion.div>
          </AnimatePresence>

          <div className="mt-6 flex items-center gap-3">
            {step > 1 && <Button variant="outline" className="rounded-full" onClick={() => setStep((s) => s - 1)} data-testid="signup-back-btn"><ArrowLeft className="mr-1 h-4 w-4" /> Back</Button>}
            {step < 3 ? (
              <Button className="ml-auto rounded-full" onClick={next} data-testid="signup-next-btn">Continue <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
            ) : (
              <Button className="ml-auto rounded-full" onClick={submit} disabled={loading} data-testid="signup-submit-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Start free trial <ArrowRight className="ml-1.5 h-4 w-4" /></>}
              </Button>
            )}
          </div>
        </div>
        <p className="mt-6 text-center text-sm text-muted-foreground">
          Already have an account? <Link to="/login" className="font-medium text-primary hover:underline" data-testid="go-login-link">Log in</Link>
        </p>
      </motion.div>
    </div>
  );
}
